import { message } from 'antd';
import React, { createContext, ReactNode, useCallback, useContext } from 'react';

/**
 * 自定义消息配置接口
 * 用于定义消息提示组件的可配置项
 */
interface CustomMessageConfig {
  duration?: number; // 消息显示的持续时间，单位为毫秒
  showIcon?: boolean; // 是否显示消息类型的图标
  prefix?: string; // 消息内容的前缀文本
  skipDuplicate?: boolean; // 是否跳过重复的消息内容
}

/**
 * 消息提示API接口定义
 * 提供了多种消息提示类型的调用方式，包括函数式调用和不同类型的消息方法
 */
interface MessageApi {
  /**
   * 函数式调用方法
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  (content: string, duration?: number): void; // 函数式调用
  /**
   * 打开普通消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  open: (content: string, duration?: number) => void;
  /**
   * 打开成功消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  success: (content: string, duration?: number) => void;
  /**
   * 打开错误消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  error: (content: string, duration?: number) => void;
  /**
   * 打开警告消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  warning: (content: string, duration?: number) => void;
  /**
   * 打开信息消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  info: (content: string, duration?: number) => void;
  /**
   * 打开加载中消息提示
   * @param content 消息内容
   * @param duration 消息显示时长，可选，单位毫秒
   */
  loading: (content: string, duration?: number) => void;
  /**
   * 销毁/关闭所有消息提示
   */
  destroy: () => void;
}

// 消息拦截器类型
export interface MessageInterceptor {
  (content: string, type: keyof MessageApi, config?: CustomMessageConfig): boolean | string;
}

// 默认配置消息
const DEFAULTCONFIG = {
  top: 100,
  duration: 1,
  maxCount: 3,
  prefixCls: 'jf-message',
};

// 独立的全局消息工具类
class GlobalMessageTool {
  private static instance: GlobalMessageTool;
  static getInstance(): GlobalMessageTool {
    if (!GlobalMessageTool.instance) {
      GlobalMessageTool.instance = new GlobalMessageTool();
      // 全局配置消息位置
      message.config(DEFAULTCONFIG);
    }
    return GlobalMessageTool.instance;
  }
  destroy = message.destroy;
  private interceptors: MessageInterceptor[] = [];
  private defaultConfig: CustomMessageConfig = {
    duration: 1,
    showIcon: true,
    skipDuplicate: true,
  };

  addInterceptor(interceptor: MessageInterceptor) {
    this.interceptors.push(interceptor);
  }

  setDefaultConfig(config: CustomMessageConfig) {
    this.defaultConfig = { ...this.defaultConfig, ...config };
  }

  // 函数式调用方法
  call = (content: string, duration?: number) => {
    const type = 'open'; // 默认使用 info 类型
    this.handleMessage(type as keyof MessageApi, content, duration);
  };

  open = (content: string, duration?: number) => {
    this.handleMessage('open', content, duration);
  };

  success = (content: string, duration?: number) => {
    this.handleMessage('success', content, duration);
  };

  error = (content: string, duration = 3) => {
    this.handleMessage('error', content, duration);
  };

  warning = (content: string, duration = 3) => {
    this.handleMessage('warning', content, duration);
  };

  info = (content: string, duration?: number) => {
    this.handleMessage('info', content, duration);
  };

  loading = (content: string, duration?: number) => {
    this.handleMessage('loading', content, duration);
  };
  clearInterceptors() {
    this.interceptors = [];
  }
  private handleMessage(type: keyof MessageApi, content: string, duration?: number) {
    if (type === 'open') {
      message[type]({
        content,
        duration: duration ?? this.defaultConfig.duration,
      });

      return;
    }

    // 使用 message 的静态方法
    message[type](content, duration);
  }
}

// 创建全局实例
const globalMessageInstance = GlobalMessageTool.getInstance();

// 创建函数式调用的消息 API
const messageApi = ((content: string, duration?: number) => {
  globalMessageInstance.call(content, duration ?? 1);
}) as MessageApi;

// 将方法绑定到函数对象上
messageApi.success = globalMessageInstance.success;
messageApi.error = globalMessageInstance.error;
messageApi.warning = globalMessageInstance.warning;
messageApi.info = globalMessageInstance.info;
messageApi.loading = globalMessageInstance.loading;
messageApi.destroy = globalMessageInstance.destroy;

// 导出全局实例
export { messageApi };

// 创建上下文
const MessageContext = createContext<MessageApi | null>(null);

interface MessageProviderProps {
  children: ReactNode;
  interceptors?: MessageInterceptor[];
  defaultConfig?: CustomMessageConfig;
}

// 消息提供者组件
export const MessageProvider: React.FC<MessageProviderProps> = ({
  children,
  interceptors = [],
  defaultConfig = {},
}) => {
  // 配置消息 Hook
  const [messageConfig, contextHolder] = message.useMessage(DEFAULTCONFIG);

  // 同步拦截器和配置到全局实例
  React.useEffect(() => {
    // 清除之前的拦截器
    globalMessageInstance.clearInterceptors();

    // 添加新的拦截器
    interceptors.forEach((interceptor) => {
      globalMessageInstance.addInterceptor(interceptor);
    });

    // 设置默认配置
    globalMessageInstance.setDefaultConfig(defaultConfig);
  }, [interceptors, defaultConfig]);

  // 创建封装后的消息 API
  const customMessageApi = useCallback((content: string, duration?: number) => {
    return globalMessageInstance.call(content, duration);
  }, []) as MessageApi;
  // 将方法绑定到上下文 API
  customMessageApi.open = (content: string, duration?: number) =>
    globalMessageInstance.open(content, duration);
  customMessageApi.success = (content: string, duration?: number) =>
    globalMessageInstance.success(content, duration);
  customMessageApi.error = (content: string, duration?: number) =>
    globalMessageInstance.error(content, duration);
  customMessageApi.warning = (content: string, duration?: number) =>
    globalMessageInstance.warning(content, duration);
  customMessageApi.info = (content: string, duration?: number) =>
    globalMessageInstance.info(content, duration);
  customMessageApi.loading = (content: string, duration?: number) =>
    globalMessageInstance.loading(content, duration);
  customMessageApi.destroy = messageConfig.destroy;

  return (
    <MessageContext.Provider value={customMessageApi}>
      {contextHolder}
      {children}
    </MessageContext.Provider>
  );
};

// 使用消息的 Hook
export const useMessage = (): MessageApi => {
  const context = useContext(MessageContext);
  if (!context) {
    throw new Error('useMessage must be used within a MessageProvider');
  }
  return context;
};

/**
 * 示例用法：
 * 组件
 * import { useMessage } from "@/context/MessageContext";
 * const messageApi = useMessage();
 * messageApi('复制成功')
 *
 * 工具
 * import { messageApi } from '@/context/MessageContext';
 * messageApi('复制成功', 1) // 纯文字弱提示
 * messageApi.success('复制成功', 1) // 成功图标弱提示
 * messageApi.error('复制失败')
 * messsageApi.info('复制成功')
 */
